// clearance-results-3d.js

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { BOX_EDGES } from '../../../app/modules/trailer-clearance/src/geometry/box-edges.mjs';

let scene, camera, renderer, controls;
let vehicleGroup = null;

let animateId = null;
let userHasInteracted = false;
let sceneInitialized = false;

// Module-scope home view state so we have a single source of truth
let homePosition = new THREE.Vector3(); // where the camera should sit
let homeTarget = new THREE.Vector3(0, 0, 0); // what the camera should look at (origin)

export const isSceneInitialized = () => sceneInitialized;

/**
 * Initialize the 3D scene (called once).
 */

// // This function initializes the Three.js scene, camera, and renderer.
// called on tab show once
export function initScene(containerId) {
   //console.log('Initializing 3D scene in container:', containerId);
   const container = document.getElementById(containerId);
   if (!container) {
      console.error(`3D container "${containerId}" not found.`);
      return;
   }

   const width = container.clientWidth;
   const height = container.clientHeight;
   if (width === 0 || height === 0) {
      console.error('3D container has zero size. Layout not ready yet.');
      return;
   }

   // Assign to module-scope, not local constants
   scene = new THREE.Scene();
   scene.background = new THREE.Color(0xf5f5f5);

   camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
   renderer = new THREE.WebGLRenderer({ antialias: true });
   container.appendChild(renderer.domElement);
   renderer.scene = scene;
   renderer.camera = camera;

   setupResponsiveThreeJS(renderer, camera, `#${containerId}`);

   controls = new OrbitControls(camera, renderer.domElement);
   controls.enableDamping = true;
   controls.dampingFactor = 0.1;
   controls.rotateSpeed = 0.6;
   controls.addEventListener('start', () => {
      userHasInteracted = true;
   });

   scene.add(new THREE.AmbientLight(0xffffff, 0.9));
   sceneInitialized = true;
   animate(); // uses the shared module-scope variables
}

/**
 * Persistent render loop.
 */
function animate(time) {
   requestAnimationFrame(animate);
   controls.update();

   // Animate pulse-tagged markers
   const scale = 1 + 0.25 * Math.sin(time * 0.005); // smooth pulse
   scene.traverse((obj) => {
      if (obj.userData.pulse) {
         obj.scale.set(scale, scale, scale);
      }
   });

   renderer.render(scene, camera);
}

export function updateSceneSimple(allBoxes, collisionPairs) {
   if (!scene) {
      console.log('3D scene not initialized.');
      return;
   }

   // Remove old group
   if (vehicleGroup) {
      scene.remove(vehicleGroup);
      vehicleGroup.traverse((obj) => {
         if (obj.geometry) obj.geometry.dispose();
         if (obj.material) obj.material.dispose();
      });
      vehicleGroup = null;
   }

   // Build new group
   vehicleGroup = new THREE.Group();

   allBoxes.forEach((box, idx) => {
      if (!box.corners || box.corners.length !== 8) {
         console.warn(`Box ${box.id} missing corners`);
         return;
      }
      const color = boxColor(idx);
      const mesh = makeBoxFromCorners(box.corners, color);
      vehicleGroup.add(mesh);
   });

   if (collisionPairs && collisionPairs.length > 0) {
      collisionPairs.forEach((pair) => {
         if (pair.intersectionPoints && pair.intersectionPoints.length > 0) {
            const markerGroup = makeCollisionMarkers(pair.intersectionPoints);
            vehicleGroup.add(markerGroup);
         }
      });
   }

   // --- Center group at origin ONCE (so rotation feels natural) ---
   const bbox = new THREE.Box3().setFromObject(vehicleGroup);
   const center = bbox.getCenter(new THREE.Vector3());
   const size = bbox.getSize(new THREE.Vector3());
   vehicleGroup.position.set(-center.x, -center.y, -center.z);

   // Add to scene (must happen before computing home distance)
   scene.add(vehicleGroup);

   // --- Compute and save a stable "home" camera position based on bbox size ---
   const maxDim = Math.max(size.x, size.y, size.z);
   const distance = maxDim * 2.0; // back off enough to see everything
   homeTarget.set(0, 0, 0);
   homePosition.set(1, 1, 1).normalize().multiplyScalar(distance);

   // If user hasn't interacted yet, apply the home view now
   if (!userHasInteracted) {
      applyHomeView();
   }
}

/**
 * Create small sphere markers at each collision point.
 * @param {Array} points - Array of {x, y, z} collision points
 * @param {THREE.Color} [color=0xff0000] - Optional color override
 * @returns {THREE.Group} group of marker meshes
 */
export function makeCollisionMarkers(points, color = 0xff4444) {
   const group = new THREE.Group();

   const coreRadius = 0.2; // radius of the core sphere
   const haloRadius = 0.3; // radius of the glow sphere

   points.forEach((pt) => {
      const coreMaterial = new THREE.MeshStandardMaterial({
         color,
         emissive: color,
         emissiveIntensity: 0.6,
         metalness: 0.3,
         roughness: 0.2,
      });

      const glowMaterial = new THREE.MeshStandardMaterial({
         color,
         transparent: true,
         opacity: 0.2,
         emissive: color,
         emissiveIntensity: 0.1,
      });

      const core = new THREE.Mesh(new THREE.SphereGeometry(coreRadius, 16, 16), coreMaterial);
      core.position.set(pt.x, pt.y, pt.z);
      core.userData.pulse = true; // Tag this mesh to pulse

      const halo = new THREE.Mesh(new THREE.SphereGeometry(haloRadius, 16, 16), glowMaterial);
      halo.position.copy(core.position);

      group.add(core);
      group.add(halo);
   });

   return group;
}

function makeBoxFromCorners(corners, color) {
   const geometry = new THREE.BufferGeometry();

   const positions = [];
   BOX_EDGES.forEach(([a, b]) => {
      const pa = corners[a];
      const pb = corners[b];
      positions.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
   });

   geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

   const material = new THREE.LineBasicMaterial({ color });
   return new THREE.LineSegments(geometry, material);
}

function boxColor(idx) {
   // Generate a color based on index
   const hue = (idx * 50) % 360; // Spread colors evenly
   return new THREE.Color(`hsl(${hue}, 70%, 60%)`);
}

// Sets up automatic Three.js canvas resize on window + tab changes

export function setupResponsiveThreeJS(renderer, camera, containerSelector = '#threeContainer') {
   const container = document.querySelector(containerSelector);
   if (!container) {
      console.error(`Container not found: ${containerSelector}`);
      return;
   }

   // Resize handler with captured arguments
   const resize = () => resizeThreeRenderer(renderer, camera, container);

   // Bind resize to window resize events
   window.addEventListener('resize', resize);

   // Initial sizing
   resize();
}

// Resize the renderer and camera to match the container dimensions
export function resizeThreeRenderer(renderer, camera, container) {
   if (!renderer || !camera || !container) {
      console.error('resizeThreeRenderer: Missing renderer, camera, or container');
      return;
   }

   // Get the actual visible size of the container element
   const width = container.clientWidth;
   const height = container.clientHeight;

   if (width === 0 || height === 0) {
      console.warn('resizeThreeRenderer: Container has zero width or height');
      return;
   }

   // Update renderer internal canvas resolution (pixel buffer)
   renderer.setSize(width, height, false);
   // false = do not update canvas CSS size — assume that's handled by your layout

   // Update camera perspective to match new aspect ratio
   camera.aspect = width / height;
   camera.updateProjectionMatrix();

   // Optional: render once after resize if not in a continuous animation loop
   if (renderer.scene && camera) {
      renderer.render(renderer.scene, renderer.camera);
   }
}

// Applies the saved home view to camera & controls, and updates OrbitControls' saved state
function applyHomeView() {
   if (!camera || !controls) return;

   // Set camera position and target to our canonical home values
   camera.position.copy(homePosition);
   controls.target.copy(homeTarget);

   // Ensure camera actually looks at target
   camera.lookAt(homeTarget);
   controls.update();

   // IMPORTANT: make OrbitControls' internal "reset" state match our home view
   controls.saveState();
}

// Reset camera to default centered position
export function resetView() {
   if (!scene || !camera || !controls) {
      console.warn('resetView: scene/camera/controls not ready');
      return;
   }
   // Don't touch vehicleGroup.position here—only camera & controls
   applyHomeView();

   // Do NOT flip userHasInteracted here. Leave it as-is to avoid surprise re-centers.
}
